import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { procurementService } from '../services/api';
import { COMPANY } from '../config/company';
import { falhaIgnoradaDeProposito } from '../design';
import { computeProposalTotal } from '../utils/proposalMath';
import { buildProposalFormData, ProposalFormState } from '../utils/proposalForm';
import { deriveProposalStage, DocsCheck, ProposalStage, StageState } from '../utils/proposalStage';

/**
 * Estado da PÁGINA DE TRABALHO da proposta (ProposalWorkspace).
 *
 * Este hook é o DONO ÚNICO do formulário. No editor legado o mesmo estado vive
 * em ~20 useState soltos dentro do LicitacoesTab, compartilhados com a lista e
 * com o drawer de detalhe — foi daí que veio o bug de 02/07 (fechar pelo X
 * deixava documentos BLL/margem sujos e a PRÓXIMA proposta herdava). Aqui o
 * estado nasce e morre com o `proposalId`: trocar de proposta remonta tudo a
 * partir do servidor, então herança silenciosa é impossível por construção.
 *
 * Escrita: SEMPRE via `buildProposalFormData` (utils/proposalForm) — a mesma
 * função do editor legado, pra não existir campo que um grava e o outro esquece.
 */

export interface ProposalItemForm {
  /**
   * O id do item NO PORTAL (Licitanet), gravado pela sincronização. A tela não mexe nele, mas tem de devolvê-lo: o PUT
   * da proposta SUBSTITUI os itens inteiros, e um salvar que o omitisse apagava a chave da gravação no portal.
   */
  codItem?: number | null;
  itemNumber: number;
  description: string;
  descricaoDetalhada?: string;
  quantity: number;
  unit: string;
  estimatedPrice: number;
  valorEstimadoSigiloso?: boolean;
  unitPrice: string;
  brand: string;
  model: string;
  groupNumber?: number;
  groupDescription?: string;
  requiresBrandModel?: boolean;
  requiresModel?: boolean;
  lote?: number;
  catser?: string;
  chargeType?: any;
  maxUnitPrice?: string;
  /** tratamento diferenciado do portal (Art. 4º) — informativo */
  tipoBeneficio?: number;
  tipoBeneficioNome?: string;
  /**
   * Critério de julgamento DESTE item no portal: 'desconto' = o ComprasNet
   * pede "Desconto (%)" e deriva o unitário do valor estimado. Portal-derivado
   * (vem do scrape), nunca escolhido pelo operador. Ver utils/proposalMath.ts.
   */
  priceMode?: 'unitario' | 'desconto';
  /** O percentual que vai pro portal quando priceMode='desconto'. */
  discountPercent?: string;
}

const EMPTY_FORM: ProposalFormState = {
  itemId: '', title: '', description: '', totalValue: '', status: 'rascunho',
  checklistNotes: '', validityDays: '60', observations: '', conditions: '',
  representative: '', representativeRole: '', bankInfo: COMPANY.bankInfoLine,
  brand: 'IPCOM', model: 'IPCOM CLOUD', valorRecorrente: false, contractMonths: '12',
  pdfLayout: '', pdfIncluirTributacao: false,
};

/** Declarações do Art. 60 com os defaults IPCOM (decisão 29/06/2026). */
function hydrateDeclarations(p: any): Array<{ id: string; label: string; currentValue: string }> {
  const decls = Array.isArray(p?.declarations) ? [...p.declarations] : [];
  if (decls.length > 0 && !decls.find((d: any) => d.id === 'termo_aceitacao')) {
    decls.unshift({ id: 'termo_aceitacao', label: 'Termo de Aceitação das Declarações', currentValue: 'Sim' });
  }
  const at = p?.acceptedTerms;
  const has = (id: string) => decls.some((d: any) => d.id === id);
  if (at?.equidadeGenero || at?.integridade) {
    if (!has('art60_desempate')) decls.push({ id: 'art60_desempate', label: 'Declarações para critérios de desempate (Art. 60 Lei 14.133/2021)', currentValue: 'Sim' });
    if (at.equidadeGenero && !has('art60_equidade')) decls.push({ id: 'art60_equidade', label: 'Equidade de genero (Art. 60, inciso III)', currentValue: at.equidadeGenero });
    if (at.equidadeNivel && !has('art60_enquadramento')) decls.push({ id: 'art60_enquadramento', label: 'Nivel de enquadramento', currentValue: at.equidadeNivel });
    if (at.integridade && !has('art60_integridade')) decls.push({ id: 'art60_integridade', label: 'Programa de integridade (Art. 60, inciso IV)', currentValue: at.integridade });
  } else {
    if (!has('art60_equidade')) decls.push({ id: 'art60_equidade', label: 'Equidade de genero (Art. 60, inciso III)', currentValue: 'Não' });
    if (!has('art60_integridade')) decls.push({ id: 'art60_integridade', label: 'Programa de integridade (Art. 60, inciso IV)', currentValue: 'Sim' });
  }
  return decls;
}

function hydrateItems(p: any): ProposalItemForm[] {
  const items = Array.isArray(p?.proposalItems) ? p.proposalItems : [];
  return items.map((it: any) => ({
    codItem: it.codItem ?? undefined,
    itemNumber: it.itemNumber || 0,
    description: it.description || '',
    descricaoDetalhada: it.descricaoDetalhada || undefined,
    quantity: it.quantity || 0,
    unit: it.unit || '',
    estimatedPrice: it.estimatedPrice || 0,
    valorEstimadoSigiloso: it.valorEstimadoSigiloso || undefined,
    unitPrice: it.unitPrice ? String(it.unitPrice) : '',
    brand: it.brand || '',
    model: it.model || '',
    catser: it.catser || undefined,
    groupNumber: it.groupNumber || undefined,
    groupDescription: it.groupDescription || undefined,
    requiresBrandModel: it.requiresBrandModel || undefined,
    requiresModel: it.requiresModel || undefined,
    lote: it.lote || undefined,
    chargeType: it.chargeType || undefined,
    maxUnitPrice: it.maxUnitPrice != null ? String(it.maxUnitPrice) : (it.estimatedPrice > 0 ? String(it.estimatedPrice) : undefined),
    tipoBeneficio: it.tipoBeneficio ?? undefined,
    tipoBeneficioNome: it.tipoBeneficioNome || undefined,
    priceMode: it.priceMode === 'desconto' ? 'desconto' : undefined,
    // `!= null` e não `||`: desconto 0% é uma oferta válida (ofertar o próprio
    // valor estimado) e precisa sobreviver ao reload, senão a tela reabre
    // dizendo que o item está sem preço.
    discountPercent: it.discountPercent != null ? String(it.discountPercent) : undefined,
  }));
}

function hydrateForm(p: any): ProposalFormState {
  return {
    itemId: String(p?.itemId ?? ''),
    title: p?.title || '',
    description: p?.description || '',
    totalValue: p?.totalValue ? String(p.totalValue) : '',
    status: p?.status || 'rascunho',
    checklistNotes: p?.checklistNotes || '',
    valorRecorrente: p?.valorRecorrente || false,
    contractMonths: p?.contractMonths ? String(p.contractMonths) : '12',
    pdfLayout: p?.pdfLayout || '',
    pdfIncluirTributacao: p?.pdfIncluirTributacao === true,
    validityDays: p?.validityDays ? String(p.validityDays) : '60',
    observations: p?.observations || '',
    conditions: p?.conditions || '',
    representative: p?.representative || '',
    representativeRole: p?.representativeRole || '',
    bankInfo: p?.bankInfo || COMPANY.bankInfoLine,
    brand: p?.brand || 'IPCOM',
    model: p?.model || 'IPCOM CLOUD',
  };
}

/** Snapshot comparável — base do dirty-tracking (sem autosave, por decisão). */
function snapshot(form: ProposalFormState, items: ProposalItemForm[], decls: any[], meepp: any, terms: boolean): string {
  return JSON.stringify({ form, items, decls, meepp, terms });
}

export interface UseProposalWorkspaceResult {
  loading: boolean;
  error: string | null;
  /** proposta como veio do servidor (fonte da verdade pra status/jobs) */
  proposal: any | null;
  item: any | null;
  form: ProposalFormState;
  setForm: (patch: Partial<ProposalFormState>) => void;
  items: ProposalItemForm[];
  setItems: (items: ProposalItemForm[]) => void;
  declarations: Array<{ id: string; label: string; currentValue: string }>;
  setDeclarations: (d: Array<{ id: string; label: string; currentValue: string }>) => void;
  termsAccepted: boolean;
  setTermsAccepted: (v: boolean) => void;
  meeppValue: 'Sim' | 'Não' | null;
  setMeeppValue: (v: 'Sim' | 'Não' | null) => void;
  margemPaisOrigem: string;
  setMargemPaisOrigem: (v: string) => void;
  margemEnquadramento: string;
  setMargemEnquadramento: (v: string) => void;
  pcpInfoComp: Array<{ field: string; label: string; resposta: string }>;
  setPcpInfoComp: (v: Array<{ field: string; label: string; resposta: string }>) => void;
  generatedDocs: any[];
  reloadGeneratedDocs: () => Promise<void>;
  checklist: any | null;
  checklistLoading: boolean;
  reloadChecklist: () => Promise<void>;
  /** Documentos obrigatórios do processo. `null` = portal sem upload (ComprasNet). */
  docsCheck: DocsCheck | null;
  contractMonths: number;
  total: number;
  dirty: boolean;
  saving: boolean;
  save: (opts?: { silent?: boolean }) => Promise<boolean>;
  /**
   * Grava marca OU modelo de UM item (pelo `codItem` do portal), pelo mesmo PUT do salvar. Rejeita com a frase do erro:
   * quem chama mostra. Usado pela gravação no portal (o que o Heitor completa lá vai para o cadastro do item).
   */
  salvarMarcaDoItem: (codItem: number, campo: 'brand' | 'model', valor: string) => Promise<void>;
  reload: () => Promise<void>;
  stage: StageState;
  viewStage: ProposalStage;
  setViewStage: (s: ProposalStage) => void;
}

export function useProposalWorkspace(
  proposalId: number | null,
  opts: {
    /**
     * Proposta VIVA vinda da lista do LicitacoesTab (que já faz polling).
     * O backend não expõe GET /proposals/:id — e é melhor assim: status, jobs e
     * item chegam sempre frescos do mesmo lugar que a lista, sem 2ª fonte de
     * verdade. O hook hidrata o FORMULÁRIO uma vez por id; o objeto vivo segue
     * atualizando status/jobs sem pisar no que está sendo editado.
     */
    liveProposal?: any | null;
    latestJob?: any | null;
    showToast?: (msg: string, type?: 'success' | 'error') => void;
    /** pede à lista pra refazer o fetch (após salvar/gerar/enviar) */
    onRequestReload?: () => void | Promise<void>;
  } = {},
): UseProposalWorkspaceResult {
  const { liveProposal, latestJob, showToast, onRequestReload } = opts;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** cópia hidratada (base do dirty) — o objeto EXIBIDO é o vivo da lista */
  const [hydrated, setHydrated] = useState<any | null>(null);
  const [form, setFormState] = useState<ProposalFormState>(EMPTY_FORM);
  const [items, setItemsState] = useState<ProposalItemForm[]>([]);
  const [declarations, setDeclarations] = useState<Array<{ id: string; label: string; currentValue: string }>>([]);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [meeppValue, setMeeppValue] = useState<'Sim' | 'Não' | null>('Sim');
  const [margemPaisOrigem, setMargemPaisOrigem] = useState('CHN');
  const [margemEnquadramento, setMargemEnquadramento] = useState('0');
  const [pcpInfoComp, setPcpInfoComp] = useState<Array<{ field: string; label: string; resposta: string }>>([]);
  const [generatedDocs, setGeneratedDocs] = useState<any[]>([]);
  const [checklist, setChecklist] = useState<any | null>(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewStage, setViewStage] = useState<ProposalStage>('montagem');

  /** snapshot do estado salvo — dirty = snapshot atual ≠ este */
  const savedSnapshotRef = useRef<string>('');
  /** evita re-hidratar (e perder edição) quando a lista repolla a mesma proposta */
  const hydratedIdRef = useRef<number | null>(null);

  const applyHydration = useCallback((p: any) => {
    const f = hydrateForm(p);
    const its = hydrateItems(p);
    const decls = hydrateDeclarations(p);
    const at = p?.acceptedTerms;
    const terms = at?.termoAceitacao ?? true;
    const meepp = at?.meepp ?? 'Sim';
    setHydrated(p);
    setFormState(f);
    setItemsState(its);
    setDeclarations(decls);
    setTermsAccepted(terms);
    setMeeppValue(meepp);
    setMargemPaisOrigem(at?.margemPreferencia?.paisOrigem || 'CHN');
    setMargemEnquadramento(at?.margemPreferencia?.enquadramento || '0');
    setPcpInfoComp(Array.isArray(at?.pcpInfoComplementar) ? at.pcpInfoComplementar : []);
    savedSnapshotRef.current = snapshot(f, its, decls, meepp, terms);
  }, []);

  const loadChecklistFor = useCallback(async (pid: number) => {
    setChecklistLoading(true);
    try {
      setChecklist(await procurementService.getDeclaracoesChecklist(pid));
    } catch {
      setChecklist(null);
    } finally {
      setChecklistLoading(false);
    }
  }, []);

  const loadDocsFor = useCallback(async (pid: number) => {
    try {
      const docs = await procurementService.getGeneratedDocs(pid);
      setGeneratedDocs(Array.isArray(docs) ? docs : []);
    } catch {
      setGeneratedDocs([]);
    }
  }, []);

  // Documento enviado à ClickSign vira "assinado" por webhook, e o card só
  // saberia disso no próximo carregamento da tela. Este poll pergunta o status
  // enquanto houver documento pendente e para sozinho quando não há mais.
  // (O editor antigo tinha esse poll, mas sobre um estado que o workspace não
  // alimenta — na prática ele não rodava desde que a página de trabalho passou
  // a ser o caminho principal.)
  const pendentesDeAssinatura = generatedDocs
    .filter((d: any) => d.status === 'aguardando_assinatura')
    .map((d: any) => d.id)
    .join(',');

  useEffect(() => {
    if (!pendentesDeAssinatura) return;
    const ids = pendentesDeAssinatura.split(',').map((s) => parseInt(s, 10));
    const timer = setInterval(async () => {
      for (const id of ids) {
        try {
          const r = await procurementService.getSignatureStatus(id);
          setGeneratedDocs((prev: any[]) => prev.map((d: any) => (
            d.id === id
              ? { ...d, status: r.status, _signers: r.signers, clicksignSignatureUrl: r.signatureUrl, signedAt: r.signedAt || d.signedAt }
              : d
          )));
        } catch { /* silencioso: erro de poll não vira toast */ }
      }
    }, 30000);
    return () => clearInterval(timer);
  }, [pendentesDeAssinatura]);

  // Hidratação keyed por proposalId. Trocar de proposta (ou fechar) remonta do
  // zero — nenhum resquício da anterior sobrevive.
  useEffect(() => {
    if (!proposalId) {
      hydratedIdRef.current = null;
      setHydrated(null);
      setFormState(EMPTY_FORM);
      setItemsState([]);
      setDeclarations([]);
      setGeneratedDocs([]);
      setChecklist(null);
      setPcpInfoComp([]);
      setMargemPaisOrigem('CHN');
      setMargemEnquadramento('0');
      setViewStage('montagem');
      savedSnapshotRef.current = '';
      setError(null);
      return;
    }
    if (hydratedIdRef.current === proposalId) return; // repolling da lista NÃO re-hidrata (perderia edição)
    if (!liveProposal || liveProposal.id !== proposalId) return; // espera a lista trazer a proposta
    hydratedIdRef.current = proposalId;
    setLoading(true);
    setError(null);
    applyHydration(liveProposal);
    Promise.all([loadDocsFor(proposalId), loadChecklistFor(proposalId)])
      .catch(() => { /* silencioso — as seções lidam com vazio */ })
      .finally(() => setLoading(false));
  }, [proposalId, liveProposal, applyHydration, loadDocsFor, loadChecklistFor]);

  const setForm = useCallback((patch: Partial<ProposalFormState>) => {
    setFormState((prev) => ({ ...prev, ...patch }));
  }, []);

  const setItems = useCallback((next: ProposalItemForm[]) => setItemsState(next), []);

  const contractMonths = parseInt(form.contractMonths, 10) || 12;
  const total = useMemo(() => computeProposalTotal(items as any, contractMonths), [items, contractMonths]);

  // O objeto EXIBIDO é o vivo da lista (status/jobs/item sempre frescos);
  // o hidratado é só a referência do que já foi salvo.
  const proposal = (liveProposal?.id === proposalId ? liveProposal : null) || hydrated;

  const dirty = useMemo(
    () => !!hydrated && savedSnapshotRef.current !== snapshot(form, items, declarations, meeppValue, termsAccepted),
    [hydrated, form, items, declarations, meeppValue, termsAccepted],
  );

  const item = proposal?.item || null;

  /**
   * Documentos do processo, na forma que o guard de envio consome. Vive aqui
   * (e não em cada seção) porque a tela de Envio e o botão laranja do cabeçalho
   * precisam da MESMA resposta — divergir foi o defeito original: o cabeçalho
   * liberava "Enviar proposta" enquanto a seção listava obrigatório sem arquivo.
   */
  const docsCheck = useMemo<DocsCheck | null>(() => {
    if (checklist?.exigeDocumentos !== true) return null;
    const rows: any[] = checklist?.rows || [];
    return {
      faltando: rows.filter((r) => r.obrigatorio && r.docStatus === 'nao_gerada').map((r) => r.exigencia),
      naoAssinados: rows.filter((r) => r.pendenteAssinatura).map((r) => r.exigencia),
      listaIndisponivel: checklist?.listaIndisponivel === true,
    };
  }, [checklist]);

  const stage = useMemo(() => deriveProposalStage({
    proposal: proposal ? { ...proposal, proposalItems: items } : null,
    item,
    latestJob,
    checklistSummary: checklist?.resumo || null,
    generatedDocs,
  }), [proposal, items, item, latestJob, checklist, generatedDocs]);

  // Ao abrir, mostra o estágio real; depois quem manda é o clique do usuário.
  const stageInitRef = useRef<number | null>(null);
  useEffect(() => {
    if (!proposalId || !proposal) return;
    if (stageInitRef.current === proposalId) return;
    stageInitRef.current = proposalId;
    setViewStage(stage.current);
  }, [proposalId, proposal, stage.current]); // eslint-disable-line react-hooks/exhaustive-deps

  const fdDe = useCallback((its: ProposalItemForm[]) => buildProposalFormData({
    form,
    items: its as any,
    computedTotal: computeProposalTotal(its as any, contractMonths),
    declarations,
    termsAccepted,
    meeppValue,
    margemPaisOrigem,
    margemEnquadramento,
    pcpInfoComp,
    portalOrigem: item?.portalOrigem || null,
    hasPortalTerms: !!item?.termsDeclarations,
    file: null,
    includeFile: false,
  }), [form, contractMonths, declarations, termsAccepted, meeppValue, margemPaisOrigem, margemEnquadramento, pcpInfoComp, item]);

  const save = useCallback(async (o: { silent?: boolean } = {}) => {
    if (!proposal?.id) return false;
    setSaving(true);
    try {
      const fd = buildProposalFormData({
        form,
        items: items as any,
        computedTotal: total,
        declarations,
        termsAccepted,
        meeppValue,
        margemPaisOrigem,
        margemEnquadramento,
        pcpInfoComp,
        portalOrigem: item?.portalOrigem || null,
        hasPortalTerms: !!item?.termsDeclarations,
        file: null,
        includeFile: false,
      });
      await procurementService.updateProposal(proposal.id, fd);
      savedSnapshotRef.current = snapshot(form, items, declarations, meeppValue, termsAccepted);
      setHydrated((prev: any) => (prev ? { ...prev, proposalItems: items } : prev));
      await onRequestReload?.();
      if (!o.silent) showToast?.('Proposta salva');
      return true;
    } catch (e: any) {
      showToast?.('Erro ao salvar: ' + (e?.response?.data?.message || e?.message), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }, [proposal, form, items, total, declarations, termsAccepted, meeppValue, margemPaisOrigem, margemEnquadramento, pcpInfoComp, item, showToast, onRequestReload]);

  // Os itens mais novos e a fila dos salvamentos por item: dois campos que perdem o foco em sequência não podem montar
  // o PUT da mesma base (o segundo desfaria o primeiro, já que o PUT substitui os itens inteiros).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const fdRef = useRef(fdDe);
  fdRef.current = fdDe;
  const snapRef = useRef((its: ProposalItemForm[]) => snapshot(form, its, declarations, meeppValue, termsAccepted));
  snapRef.current = (its) => snapshot(form, its, declarations, meeppValue, termsAccepted);
  const filaRef = useRef<Promise<unknown>>(Promise.resolve());

  const salvarMarcaDoItem = useCallback((codItem: number, campo: 'brand' | 'model', valor: string) => {
    const vez = filaRef.current.catch(falhaIgnoradaDeProposito('a falha do salvamento anterior já aparece no campo dela; a fila só não pode parar nela')).then(async () => {
      if (!proposal?.id) throw new Error('proposta não carregada');
      const atuais = itemsRef.current;
      if (atuais.filter((it) => it.codItem === codItem).length !== 1) throw new Error('o item não está na proposta (sincronize do portal)');
      const novos = atuais.map((it) => (it.codItem === codItem ? { ...it, [campo]: valor } : it));
      try {
        await procurementService.updateProposal(proposal.id, fdRef.current(novos));
      } catch (e: any) {
        throw new Error(e?.response?.data?.message || e?.message || 'erro ao salvar');
      }
      itemsRef.current = novos;
      setItemsState(novos);
      // O PUT leva o formulário inteiro: o que estava na tela ficou salvo, e a base do "não salvo" é ela.
      savedSnapshotRef.current = snapRef.current(novos);
      setHydrated((prev: any) => (prev ? { ...prev, proposalItems: novos } : prev));
    });
    filaRef.current = vez;
    return vez;
  }, [proposal?.id]);

  const reload = useCallback(async () => {
    if (!proposalId) return;
    await onRequestReload?.();
    await Promise.all([loadDocsFor(proposalId), loadChecklistFor(proposalId)]);
  }, [proposalId, onRequestReload, loadDocsFor, loadChecklistFor]);

  return {
    loading, error, proposal, item,
    form, setForm,
    items, setItems,
    declarations, setDeclarations,
    termsAccepted, setTermsAccepted,
    meeppValue, setMeeppValue,
    margemPaisOrigem, setMargemPaisOrigem,
    margemEnquadramento, setMargemEnquadramento,
    pcpInfoComp, setPcpInfoComp,
    generatedDocs,
    reloadGeneratedDocs: async () => { if (proposalId) await loadDocsFor(proposalId); },
    checklist, checklistLoading,
    reloadChecklist: async () => { if (proposalId) await loadChecklistFor(proposalId); },
    docsCheck,
    contractMonths, total, dirty, saving, save, salvarMarcaDoItem, reload,
    stage, viewStage, setViewStage,
  };
}
